/*
  SOCCapabilities.js
  Três capacidades principais do SOC Integrado da FMP:
  Contenção localizada, Conformidade LGPD e Análise de grandes volumes.
  Baseado no documento VoeTur — slide "Incidentes Críticos (SOC Integrado)".
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

const capabilities = [
  {
    icon: "gpp_bad",
    title: "Contenção de Ataques Localizados",
    description:
      "Em caso de ataque, o malware inserido é capaz somente de atingir um local. A separação arquitetural da MDC-SR10 V2 impede a propagação lateral, isolando a ameaça antes que ela se espalhe pela rede.",
  },
  {
    icon: "gavel",
    title: "Conformidade Simplificada com a LGPD",
    description:
      "Integração com sistemas legados e modernos — SIEM, firewalls e endpoints. Retenção de logs para análise forense e processos regulatórios, com geração automática de relatórios de conformidade.",
  },
  {
    icon: "manage_search",
    title: "Análise de Grandes Volumes de Dados",
    description:
      "Detecção, correlação e resposta automatizada a ameaças em toda a rede, com Playbooks prontos para ataques típicos — reduzindo o tempo de resposta e o impacto operacional.",
  },
];

const resilience = [
  {
    icon: "account_tree",
    title: "Arquitetura Descentralizada",
    description: "Segmentação de riscos por design — cada nó opera de forma independente, limitando o raio de impacto de qualquer incidente.",
  },
  {
    icon: "verified_user",
    title: "Confiança Zero",
    description: "Isolamento automático de dispositivos e segmentos comprometidos, com recuperação rápida sem intervenção manual.",
  },
  {
    icon: "speed",
    title: "Computação Local em Tempo Real",
    description: "Performance de ponta mesmo em localidades remotas — monitoramento contínuo sem dependência de conectividade com a nuvem.",
  },
  {
    icon: "autorenew",
    title: "Continuidade Operacional",
    description: "Operações mantidas mesmo sob ataque ativo, com mecanismos automatizados de detecção e contenção que minimizam prejuízos.",
  },
];

function CapabilityCard({ icon, title, description }) {
  return (
    <MKBox
      display="flex"
      flexDirection="column"
      borderRadius="lg"
      shadow="md"
      p={3}
      height="100%"
      sx={{
        backgroundColor: ({ palette: { white } }) => white.main,
        border: ({ borders: { borderWidth, borderColor } }) =>
          `${borderWidth[1]} solid ${borderColor}`,
        transition: "box-shadow 200ms ease",
        "&:hover": { boxShadow: ({ boxShadows: { lg } }) => lg },
      }}
    >
      <MKBox
        width="3.5rem"
        height="3.5rem"
        variant="gradient"
        bgColor="info"
        color="white"
        coloredShadow="info"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="xl"
        mb={2}
      >
        <Icon fontSize="medium">{icon}</Icon>
      </MKBox>
      <MKTypography variant="h5" mb={1}>
        {title}
      </MKTypography>
      <MKTypography variant="body2" color="text">
        {description}
      </MKTypography>
    </MKBox>
  );
}

// PropTypes
import PropTypes from "prop-types";

CapabilityCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

function SOCCapabilities() {
  return (
    <MKBox component="section" py={{ xs: 6, md: 10 }}>
      <Container>

        {/* Título */}
        <Grid container justifyContent="center" mb={6}>
          <Grid item xs={12} md={8} textAlign="center">
            <MKTypography variant="h3" mb={1}>
              SOC Integrado para ambientes críticos
            </MKTypography>
            <MKTypography variant="body2" color="text">
              A solução de SOC da FMP Soluções combina hardware dedicado com software de detecção
              e resposta — projetada para operar em ambientes distribuídos, remotos e de alta
              criticidade, garantindo continuidade mesmo sob ataques sofisticados.
            </MKTypography>
          </Grid>
        </Grid>

        {/* Três pilares principais */}
        <Grid container spacing={3} mb={8}>
          {capabilities.map((cap, i) => (
            <Grid item xs={12} md={4} key={i}>
              <CapabilityCard {...cap} />
            </Grid>
          ))}
        </Grid>

        {/* Bloco resiliência */}
        <MKBox
          borderRadius="lg"
          p={{ xs: 3, md: 5 }}
          mb={2}
          sx={{
            background: ({ palette: { gradients } }) =>
              `linear-gradient(195deg, ${gradients.dark.main}, ${gradients.dark.state})`,
          }}
        >
          <Grid container spacing={1} mb={4}>
            <Grid item xs={12} textAlign="center">
              <MKTypography variant="h4" color="white" mb={1}>
                Resiliência contra Ransomware e Latência
              </MKTypography>
              <MKTypography variant="body2" color="white" opacity={0.8}>
                Mecanismos automatizados de detecção e contenção em tempo real que garantem a
                continuidade das operações, minimizando prejuízos físicos e preservando a
                segurança mesmo diante de ataques sofisticados.
              </MKTypography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {resilience.map((item, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <MKBox display="flex" gap={2}>
                  <MKBox
                    width="2.5rem"
                    height="2.5rem"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="lg"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.15)",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: "white", fontSize: "1.25rem" }}>{item.icon}</Icon>
                  </MKBox>
                  <MKBox>
                    <MKTypography variant="h6" color="white" mb={0.5}>
                      {item.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="white" opacity={0.8}>
                      {item.description}
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </Grid>
            ))}
          </Grid>
        </MKBox>

      </Container>
    </MKBox>
  );
}

export default SOCCapabilities;
